// src/features/boe/components/programs/Program0200.jsx

import React from "react";
import { ProgramBudget } from "../../../../budget/model/program/ProgramBudget.jsx";

/**
 * Configuration object defining how Program 0200 (ART) fields,
 * data mappings, table structures, and UI behaviors should appear.
 *
 * This object is consumed by <ProgramBudget /> to generate:
 * - Approved & Actual budget tables
 * - Field groupings (positions, salaries, materials, other)
 * - Data extraction rules from Firestore blocks
 *
 * @typedef {Object} ProgramFieldDefinition
 * @property {string} key - Unique field identifier used by the UI & data layers.
 * @property {string} label - User-facing text shown in the budget table.
 * @property {"currency"|"count"|"text"} valueType - Data formatting type.
 * @property {Array<{block: string, key: string}>} sources - One or more mappings to Firestore budget data blocks.
 * @property {"positions"|"salaries"|"materials"|"other"} group - Logical section grouping.
 * @property {boolean} defaultVisible - Whether the field appears by default in the UI.
 *
 * @typedef {Object} ProgramTableDefinition
 * @property {string} id - Table identifier ("approved" or "actual").
 * @property {string} title - Table header shown in the UI.
 * @property {string} sectionKey - Firestore section key ("Approved" or "Actual").
 * @property {string[]} fields - Ordered list of field keys included in this table.
 * @property {Object<string, Object>} columnStyles - Optional per-column width constraints.
 *
 * @typedef {Object} ProgramConfig
 * @property {string} fullName - Full display name of the program.
 * @property {string} docId - Short code identifying the program in Firestore.
 * @property {ProgramFieldDefinition[]} fieldDefs - All fields that belong to this program.
 * @property {ProgramTableDefinition[]} tables - Approved/Actual table configurations.
 */

/**
 * @type {ProgramConfig}
 */
export const PROGRAM_0200_CONFIG = {
  fullName: "ART",
  docId: "0200",

  /**
   * Field definitions describing how to extract, label, and display ART program data.
   * Each field maps to 1+ Firestore blocks internally combined by ProgramBudget.
   */
  fieldDefs: [
    // POSITIONS (FTE)
    {
      key: "positionsAdmin",
      label: "Positions – Administrative",
      valueType: "count",
      sources: [{ block: "Positions", key: "Administrative" }],
      group: "positions",
      defaultVisible: false,
    },
    {
      key: "positionsTeachers",
      label: "Positions – Teachers",
      valueType: "count",
      sources: [{ block: "Positions", key: "Teachers" }],
      group: "positions",
      defaultVisible: false,
    },

    // SALARIES
    {
      key: "salariesAdmin",
      label: "Salaries – Administrative",
      valueType: "currency",
      sources: [
        { block: "Salaries", key: "Administrative" },
      ],
      group: "salaries",
      defaultVisible: false,
    },
    {
      key: "salariesTeachers",
      label: "Salaries – Teachers",
      valueType: "currency",
      sources: [{ block: "Salaries", key: "Teachers" }],
      group: "salaries",
      defaultVisible: false,
    },

    // 🎨 Field Trips show up inconsistently, so we support both blocks
    {
      key: "fieldTrips",
      label: "Field Trips",
      valueType: "currency",
      sources: [
        { block: "Salaries", key: "Field Trips" },
        { block: "Supplies / Materials", key: "Field Trips" },
      ],
      group: "salaries",
      defaultVisible: false,
    },

    // SUPPLIES / MATERIALS
    {
      key: "instructionalMaterials",
      label: "Instructional Materials",
      valueType: "currency",
      sources: [
        { block: "Supplies / Materials", key: "Instructional Materials" },
      ],
      group: "materials",
      defaultVisible: false,
    },

    // OTHER
    {
      key: "duesFees",
      label: "Dues / Fees",
      valueType: "currency",
      sources: [{ block: "Other", key: "Dues / Fees" }],
      group: "other",
      defaultVisible: false,
    },
  ],

  /**
   * Table definitions for Approved & Actual budget views.
   * These control:
   * - Which fields appear
   * - Order of fields
   * - Column widths
   */
  tables: [
    {
      id: "approved",
      title: "Approved Budget",
      sectionKey: "Approved",
      fields: [
        "positionsAdmin",
        "positionsTeachers",
        "salariesAdmin",
        "salariesTeachers",
        "fieldTrips",
        "instructionalMaterials",
        "duesFees",
      ],
      columnStyles: {
        year: { width: "110px", minWidth: "110px" },
        positionsAdmin: { minWidth: "120px" },
        positionsTeachers: { minWidth: "140px" },
        salariesAdmin: { minWidth: "160px" },
        salariesTeachers: { minWidth: "130px" },
        fieldTrips: { minWidth: "110px" },
        instructionalMaterials: { minWidth: "120px" },
        duesFees: { minWidth: "100px" },
      },
    },
    {
      id: "actual",
      title: "Actual Spending",
      sectionKey: "Actual",
      fields: [
        "positionsAdmin",
        "positionsTeachers",
        "salariesAdmin",
        "salariesTeachers",
        "fieldTrips",
        "instructionalMaterials",
        "duesFees",
      ],
      columnStyles: {
        year: { width: "110px", minWidth: "110px" },
        positionsAdmin: { minWidth: "120px" },
        positionsTeachers: { minWidth: "140px" },
        salariesAdmin: { minWidth: "160px" },
        salariesTeachers: { minWidth: "130px" },
        fieldTrips: { minWidth: "110px" },
        instructionalMaterials: { minWidth: "120px" },
        duesFees: { minWidth: "100px" },
      },
    },
  ],
};

/**
 * Program 0200 – ART
 *
 * Renders the configuration into a <ProgramBudget /> component,
 * providing shared editing capabilities, Firestore linking,
 * and year-specific data expansion.
 *
 * @component
 * @param {Object} props - Passed through to <ProgramBudget />.
 * @param {boolean} props.isEditing - Whether fields should render in edit mode.
 * @param {Function} props.setBoeData - Callback used to record in-progress edits.
 * @returns {JSX.Element}
 */
export function Program0200(props) {
  return (
    <ProgramBudget
      programId="0200"
      config={PROGRAM_0200_CONFIG}
      {...props}
    />
  );
}
